import {
  EventKind,
  EventPosition,
  EventsApi,
  type LanguageCode
} from '@linktivity/tata';
import langStore from '~/stores/modules/lang';
import config from './config';

export type { EventKind, EventPosition };

export interface TrackEventParams {
  kind: EventKind;
  position: EventPosition;
  poiId?: string;
  productId?: string;
  bookingId?: string;
  languageCode?: string;
  text?: string;
  count?: number;
  durationMs?: number;
}

const eventsApi = new EventsApi(config);

const BATCH_KINDS: ReadonlySet<EventKind> = new Set([
  EventKind.EventKindSwipe,
  EventKind.EventKindClick
]);
const BATCH_DELAY_MS = 10_000;

interface Buffered {
  params: TrackEventParams;
  count: number;
}

const buffer = new Map<string, Buffered>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function bufferKey(p: TrackEventParams): string {
  return [
    p.kind,
    p.position,
    p.poiId ?? '',
    p.productId ?? '',
    p.bookingId ?? '',
    p.languageCode ?? '',
    p.text ?? ''
  ].join('\x00');
}

function send(params: TrackEventParams, count: number): void {
  const bookingId = params.bookingId;
  const languageCode =
    params.languageCode ?? langStore.languageCode ?? undefined;
  const poiId = params.poiId;

  eventsApi
    .recordEvent({
      body: {
        bookingId,
        languageCode: languageCode as LanguageCode | undefined,
        kind: params.kind,
        position: params.position,
        poiId,
        productId: params.productId,
        text: params.text,
        count,
        durationMs: params.durationMs
      }
    })
    .catch(() => {});
}

function flush(): void {
  flushTimer = null;
  const entries = [...buffer.values()];
  buffer.clear();
  for (const { params, count } of entries) {
    send(params, count);
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flush();
    }
  });
}

export function trackEvent(params: TrackEventParams): void {
  if (BATCH_DELAY_MS > 0 && BATCH_KINDS.has(params.kind)) {
    const key = bufferKey(params);
    const existing = buffer.get(key);
    buffer.set(key, { params, count: (existing?.count ?? 0) + 1 });
    if (!flushTimer) {
      flushTimer = setTimeout(flush, BATCH_DELAY_MS);
    }
    return;
  }
  send(params, params.count ?? 1);
}
